/* eslint-disable no-useless-escape */
import {DocumentRoutes, ProfileRoutes, Routes} from "@buildwithflux/core";
import {GeneralTrackingEvents} from "@buildwithflux/models";
import {isProductionEnvironment} from "@buildwithflux/shared";
import CssBaseline from "@material-ui/core/CssBaseline";
import React, {Suspense, useCallback, useEffect} from "react";
import {Redirect, Route, RouteComponentProps, Router, Switch} from "react-router-dom";

import GlobalGuybrush from "./components/common/components/guybrush/GlobalGuybrush";
import Dialogs from "./components/common/components/dialog/Dialogs";
import {IntercomSetup} from "./components/common/components/intercom/IntercomSetup";
import {lazyRetry} from "./components/common/components/lazyLoader";
import FullPageLoader from "./components/common/components/loader/FullPageLoader";
import AppUpdateSnackbar from "./components/common/components/Snackbars/AppUpdate";
import {useUserHandleCookie} from "./components/common/hooks/useUserHandleCookie";
import history from "./components/helpers/history";
import useTheme from "./components/helpers/hooks/UseTheme";
import ScrollToTop from "./components/helpers/ScrollToTop";
import SetVersion from "./components/helpers/SetVersion";
import TrackPayments from "./components/helpers/TrackPayments";
import ConditionalRoute from "./components/pages/authentication/ConditionalRoute";
import FluxClaimsRoute from "./components/pages/authentication/FluxClaimsRoute";
import {RouteWithErrorBoundary} from "./components/pages/authentication/RouteWithErrorBoundary";
import {useFluxServices} from "./injection/hooks";
import {blockUntilAuthReady} from "./modules/auth/hooks";
import {useFluxConsoleHelpers} from "./modules/common/hooks/useFluxConsoleHelpers";
import {useFluxLogger} from "./modules/common/hooks/useFluxLogger";
import {FluxSnackbarProvider} from "./modules/common/providers/FluxSnackbarProvider";
import PerformanceMetricsController from "./modules/feature_flags/PerformanceMetricsController";
import externalUrls from "./resources/ExternalUrls";

const LogIn = lazyRetry(() => import(/* webpackChunkName: 'LogIn' */ "./components/pages/authentication/LogIn"));
const SignUp = lazyRetry(() => import(/* webpackChunkName: 'SignUp' */ "./components/pages/authentication/SignUp"));
const LogOut = lazyRetry(() => import(/* webpackChunkName: 'LogOut' */ "./components/pages/authentication/LogOut"));

const AccountUpgrade = lazyRetry(
    () => import(/* webpackChunkName: 'AccountUpgrade' */ "./components/pages/account/AccountUpgrade"),
);
const CreateOrganization = lazyRetry(
    () => import(/* webpackChunkName: 'CreateOrganization' */ "./components/pages/account/CreateOrganization"),
);
const ResetPassword = lazyRetry(
    () => import(/* webpackChunkName: 'ResetPassword' */ "./components/pages/authentication/ResetPassword"),
);
const VerifyEmail = lazyRetry(
    () => import(/* webpackChunkName: 'VerifyEmail' */ "./components/pages/authentication/VerifyEmail"),
);
const Document = lazyRetry(() => import(/* webpackChunkName: 'Document' */ "./components/pages/document/Document"));
const Profile = lazyRetry(() => import(/* webpackChunkName: 'Profile' */ "./components/pages/profile/Profile"));
const CreateDocument = lazyRetry(
    () =>
        import(
            /* webpackChunkName: 'CreateDocument' */
            "./components/pages/document/components/create_document/CreateDocument"
        ),
);

const ForkDocument = lazyRetry(
    () =>
        import(
            /* webpackChunkName: 'ForkDocument' */
            "./components/pages/document/components/fork_document/ForkDocument"
        ),
);
const CloneDocument = lazyRetry(
    () =>
        import(
            /* webpackChunkName: 'CloneDocument' */
            "./components/pages/document/components/clone_document/CloneDocument"
        ),
);
const NotFound = lazyRetry(
    () => import(/* webpackChunkName: 'NotFound' */ "./components/pages/errors/404/PageNotFound"),
);
const LandingPage = lazyRetry(
    () => import(/* webpackChunkName: 'LandingPage' */ "./components/pages/landingpage/LandingPage"),
);
const Search = lazyRetry(() => import(/* webpackChunkName: 'Sitemap' */ "./components/pages/search/Search"));
const Sitemap = lazyRetry(() => import(/* webpackChunkName: 'Sitemap' */ "./components/pages/sitemap/Sitemap"));
const RedirectPage = lazyRetry(() => import(/* webpackChunkName: 'Redirect' */ "./components/pages/redirect/Redirect"));
const InternalRouter = lazyRetry(
    () => import(/* webpackChunkName: 'Internal' */ "./components/pages/internal/InternalRouter"),
);

const SitemapCategory = lazyRetry(
    () => import(/* webpackChunkName: 'SitemapCategory' */ "./components/pages/sitemap/pages/category/Category"),
);
const SitemapSubcategory = lazyRetry(
    () =>
        import(
            /* webpackChunkName: 'SitemapSubcategory' */ "./components/pages/sitemap/pages/sub_category/SubCategory"
        ),
);
const SitemapMarkdown = lazyRetry(
    () => import(/* webpackChunkName: 'SitemapMarkdown' */ "./components/pages/sitemap/Sitemap.md"),
);
const SitemapCategoryMarkdown = lazyRetry(
    () =>
        import(
            /* webpackChunkName: 'SitemapCategoryMarkdown' */ "./components/pages/sitemap/pages/category/Category.md"
        ),
);
const SitemapSubcategoryMarkdown = lazyRetry(
    () =>
        import(
            /* webpackChunkName: 'SitemapSubcategoryMarkdown' */ "./components/pages/sitemap/pages/sub_category/SubCategory.md"
        ),
);
const ProfileMarkdown = lazyRetry(
    () => import(/* webpackChunkName: 'ProfileMarkdown' */ "./components/pages/profile/Profile.md"),
);
const StarredMarkdown = lazyRetry(
    () => import(/* webpackChunkName: 'StarredMarkdown' */ "./components/pages/profile/Starred.md"),
);
const ProjectMarkdown = lazyRetry(
    () => import(/* webpackChunkName: 'ProjectMarkdown' */ "./components/pages/project/Project.md"),
);

if (isProductionEnvironment()) {
    // eslint-disable-next-line no-console
    console.info(`

         /$$$$$$$$ /$$
        | $$_____/| $$
       <  $$      | $$ /$$   /$$ /$$   /$$
        > $$$$$   | $$| $$  | $$|  $$ /$$/
       <  $$__/   | $$| $$  | $$ \  $$$$/
        > $$      | $$| $$  | $$  >$$  $$
       <  $$      | $$|  $$$$$$/ /$$/\  $$
        |__/      |__/ \______/ |__/  \__/


        We're hiring! https://jobs.flux.ai/
    `);
}

const loading = <FullPageLoader delay={450} showLongWaitExcuse={true} surface={"app_loader"} showNavBar={false} />;

const defaultRouteRender = ({staticContext}: RouteComponentProps<{[key: string]: string | undefined}>) => {
    if (staticContext) {
        staticContext.statusCode = 404;
    }
    return <NotFound />;
};

const getCloneDocumentForTemplates = (props: RouteComponentProps) => <CloneDocument {...props} mode={"template"} />;

const getCloneDocumentForExamples = (props: RouteComponentProps) => <CloneDocument {...props} mode={"example"} />;
const getCloneDocumentForLearning = (props: RouteComponentProps) => (
    <CloneDocument {...props} mode={"clone"} isLearningDocument={true} />
);

const RESET_PASSWORD_ROUTES = [Routes.RESET_PASSWORD, Routes.RESET_PASSWORD_CALLBACK];

// Please keep your code out of here unless it truly need to run on ALL pages!
const AppComponent: React.FC = () => {
    // ATTENTION: the damn order of the code here matters!
    const {analyticsStorage, trialReactivationService, fluxAuthService} = useFluxServices();

    // This is done for eager initialization of FluxAuthService to improve debug logging
    void fluxAuthService;

    // This is done for eager initialization of TrialReactivationService which subscribes to user changes
    void trialReactivationService;

    useFluxLogger();
    useTheme();
    useUserHandleCookie();

    useEffect(() => {
        // initial page load
        analyticsStorage.logEvent(GeneralTrackingEvents.pageView);

        analyticsStorage.logEvent(GeneralTrackingEvents.appLoad, {
            app_load_time: Date.now() - window.performance.timing.navigationStart,
        });

        history.listen(() => {
            analyticsStorage.logEvent(GeneralTrackingEvents.pageView);
        });
    }, [analyticsStorage]);

    const redirectToCopilotDocs = useCallback(() => {
        window.location.href = externalUrls.documentation.copilot_docs;
        return null;
    }, []);

    useFluxConsoleHelpers();

    return (
        <>
            <CssBaseline />
            <SetVersion />
            <IntercomSetup />
            <PerformanceMetricsController />

            <Router history={history}>
                <ScrollToTop />
                <TrackPayments />
                <FluxSnackbarProvider>
                    <Suspense fallback={loading}>
                        <Switch>
                            <Route path={Routes.COPILOT} exact render={redirectToCopilotDocs} />

                            <RouteWithErrorBoundary exact path={Routes.LANDING} component={LandingPage} />

                            <ConditionalRoute exact path={Routes.LOG_IN} isLogIn={true} component={LogIn} />
                            {/* It's important that all users can access the log out action, not just fully authenticated ones */}
                            <RouteWithErrorBoundary exact path={Routes.LOG_OUT} component={LogOut} />

                            {/* These are legacy routes to keep /signin and /signout working */}
                            <Route exact path={Routes.SIGN_IN}>
                                <Redirect to={Routes.LOG_IN} />
                            </Route>
                            <Route exact path={Routes.SIGN_OUT}>
                                <Redirect to={Routes.LOG_OUT} />
                            </Route>

                            <ConditionalRoute isSignUp={true} exact path={Routes.SIGN_UP} component={SignUp} />

                            <RouteWithErrorBoundary exact path={Routes.ACCOUNT_UPGRADE} component={AccountUpgrade} />
                            <RouteWithErrorBoundary
                                exact
                                path={Routes.CREATE_ORGANIZATION}
                                component={CreateOrganization}
                            />

                            <RouteWithErrorBoundary exact path={Routes.SEARCH} component={Search} />
                            {/* Markdown routes must come before HTML routes to match .md URLs correctly */}
                            <RouteWithErrorBoundary exact path={`${Routes.SITEMAP}.md`} component={SitemapMarkdown} />
                            <RouteWithErrorBoundary
                                exact
                                path={`${Routes.SITEMAP_CATEGORY}.md`}
                                component={SitemapCategoryMarkdown}
                            />
                            <RouteWithErrorBoundary
                                exact
                                path={`${Routes.SITEMAP_SUBCATEGORY}.md`}
                                component={SitemapSubcategoryMarkdown}
                            />
                            <RouteWithErrorBoundary exact path={Routes.SITEMAP} component={Sitemap} />
                            <RouteWithErrorBoundary exact path={Routes.SITEMAP_CATEGORY} component={SitemapCategory} />
                            <RouteWithErrorBoundary
                                exact
                                path={Routes.SITEMAP_SUBCATEGORY}
                                component={SitemapSubcategory}
                            />

                            <RouteWithErrorBoundary exact path={RESET_PASSWORD_ROUTES} component={ResetPassword} />
                            <RouteWithErrorBoundary exact path={Routes.VERIFY_EMAIL} component={VerifyEmail} />
                            <RouteWithErrorBoundary exact path={Routes.REDIRECT} component={RedirectPage} />
                            <ConditionalRoute
                                isProtected={true}
                                exact
                                path={Routes.CREATE_DOCUMENT}
                                component={CreateDocument}
                            />
                            <ConditionalRoute
                                isProtected={true}
                                exact
                                path={Routes.CREATE_DOCUMENT_FROM_TEMPLATE}
                                render={getCloneDocumentForTemplates}
                            />
                            <RouteWithErrorBoundary exact path={Routes.FORK_DOCUMENT} component={ForkDocument} />
                            <ConditionalRoute
                                isProtected={true}
                                exact
                                path={Routes.CLONE_DOCUMENT}
                                component={CloneDocument}
                            />
                            <ConditionalRoute
                                isProtected={true}
                                exact
                                path={Routes.CLONE_EXAMPLE_DOCUMENT}
                                component={getCloneDocumentForExamples}
                            />
                            <ConditionalRoute
                                isProtected={true}
                                exact
                                path={Routes.CLONE_LEARNING_DOCUMENT}
                                component={getCloneDocumentForLearning}
                            />

                            <FluxClaimsRoute
                                path={Routes.INTERNAL_DYNAMIC}
                                component={InternalRouter}
                                requiredClaim="internal"
                            />

                            {/* Profile and project markdown routes must come before ProfileRoutes/DocumentRoutes to match .md URLs correctly */}
                            <RouteWithErrorBoundary exact path="/:handle/starred.md" component={StarredMarkdown} />
                            <RouteWithErrorBoundary exact path="/:handle.md" component={ProfileMarkdown} />
                            <RouteWithErrorBoundary
                                exact
                                path="/:handle/:documentSlug.md"
                                component={ProjectMarkdown}
                            />
                            <ConditionalRoute exact path={ProfileRoutes} component={Profile} />
                            <ConditionalRoute exact path={DocumentRoutes} component={Document} />

                            <RouteWithErrorBoundary exact path="*" render={defaultRouteRender} component={NotFound} />
                        </Switch>
                        <Dialogs />
                        <AppUpdateSnackbar />
                        <GlobalGuybrush />
                    </Suspense>
                </FluxSnackbarProvider>
            </Router>
        </>
    );
};

export default blockUntilAuthReady(AppComponent);
